/**
 * flatcValidator — runs flatc --conform in the browser via WASM.
 *
 * Uses the Emscripten-compiled flatc binary with MEMFS virtual filesystem
 * to validate that modified .fbs schemas are backward-compatible with the
 * originals. If the conformity check fails, the PR is blocked.
 */

interface FlatcModule {
  FS: {
    writeFile(path: string, data: string): void;
    readFile(path: string, opts?: { encoding: string }): string;
    mkdir(path: string): void;
    unlink(path: string): void;
  };
  callMain(args: string[]): number;
}

let flatcModule: FlatcModule | null = null;
let initPromise: Promise<void> | null = null;

/** Initialize the flatc WASM module. Safe to call multiple times. */
export async function initFlatc(): Promise<void> {
  if (flatcModule) return;
  if (initPromise) {
    await initPromise;
    return;
  }

  initPromise = (async () => {
    try {
      // Dynamic import — the WASM files are in src/wasm/flatc/ (gitignored,
      // built locally via `npm run build:flatc-wasm`; not bundled in CI).
      // eslint-disable-next-line import/no-unresolved, import/extensions
      const mod = await import("../../../wasm/flatc/flatc.mjs");
      const createModule = mod.default as (opts?: Record<string, unknown>) => Promise<FlatcModule>;
      flatcModule = await createModule({
        print: () => {},
        printErr: () => {},
      });
    } catch (err) {
      initPromise = null;
      throw err;
    }
  })();

  await initPromise;
}

export interface ConformityResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validate that a modified .fbs schema conforms to the original using flatc --conform.
 *
 * @param originalContent The original .fbs file content (before modifications)
 * @param modifiedContent The modified .fbs file content (after modifications)
 * @param fileName Optional filename for better error messages
 */
export async function validateSchemaConformity(
  originalContent: string,
  modifiedContent: string,
  fileName = "schema.fbs",
): Promise<ConformityResult> {
  await initFlatc();
  if (!flatcModule) {
    return { valid: false, errors: ["flatc WASM module failed to initialize"] };
  }

  const errors: string[] = [];
  const originalPath = `/tmp/original_${fileName}`;
  const modifiedPath = `/tmp/modified_${fileName}`;

  try {
    // Write both schemas to the MEMFS virtual filesystem
    flatcModule.FS.writeFile(originalPath, originalContent);
    flatcModule.FS.writeFile(modifiedPath, modifiedContent);

    // Capture output from this run by creating a fresh module instance
    const captured: string[] = [];
    // eslint-disable-next-line import/no-unresolved, import/extensions
    const mod = await import("../../../wasm/flatc/flatc.mjs");
    const createModule = mod.default as (opts?: Record<string, unknown>) => Promise<FlatcModule>;
    const runModule = await createModule({
      print: (text: unknown) => captured.push(String(text)),
      printErr: (text: unknown) => captured.push(String(text)),
    });

    // Write files to the run module's FS
    runModule.FS.writeFile(originalPath, originalContent);
    runModule.FS.writeFile(modifiedPath, modifiedContent);

    // flatc --conform <old_schema> -- <new_schema>
    const exitCode = runModule.callMain(["--conform", originalPath, "--", modifiedPath]);

    if (exitCode !== 0) {
      errors.push(...captured.filter((line) => String(line).trim().length > 0));
      if (errors.length === 0) {
        errors.push(`flatc --conform failed with exit code ${exitCode} (no error details)`);
      }
    }

    return { valid: exitCode === 0, errors };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : "Unknown error";
    return {
      valid: false,
      errors: [`flatc validation error: ${msg}`, ...errors],
    };
  }
}

/**
 * Validate multiple schema file changes at once.
 */
export async function validateAllChanges(
  changes: {
    path: string;
    originalContent: string;
    modifiedContent: string;
  }[],
): Promise<{ allValid: boolean; results: Record<string, ConformityResult> }> {
  const results: Record<string, ConformityResult> = {};
  let allValid = true;

  for (let i = 0; i < changes.length; i += 1) {
    const change = changes[i];
    const fileName = change.path.split("/").pop() || "schema.fbs";
    // eslint-disable-next-line no-await-in-loop
    const result = await validateSchemaConformity(
      change.originalContent,
      change.modifiedContent,
      fileName,
    );
    results[change.path] = result;
    if (!result.valid) allValid = false;
  }

  return { allValid, results };
}
